import type { ReactNode } from 'react';

/**
 * The spinner beside a line saying Sigora is working. Decoration only: the words beside it say
 * what is happening, and with motion reduced it stands still as a partly drawn ring, which still
 * reads as a loading mark.
 */
export const PendingMark = () => <span aria-hidden="true" className="pending-mark" />;

export interface PendingProps {
  /** What Sigora is doing, in the step's own words. */
  children: ReactNode;
  /** The site's own class, for its spacing and type. */
  className?: string;
  testId?: string;
  /**
   * Whether the line announces itself, as a status. False where it already sits in a live region
   * that speaks for it — the step's own `aria-live` wrapper, which also carries the lines that
   * replace it — so a screen reader hears the words once rather than twice.
   */
  live?: boolean;
}

/**
 * A line saying Sigora is generating something, with the spinner in front of it. A status unless
 * told it is inside a live region already, so a screen reader hears the words when they appear
 * without being moved to them.
 */
export const Pending = ({ children, className, testId, live = true }: PendingProps) => (
  <p
    className={className === undefined ? 'pending' : `pending ${className}`}
    data-test-id={testId}
    role={live ? 'status' : undefined}
  >
    <PendingMark />
    {children}
  </p>
);
